/**
 * useProjectMode — resolve which V3 project mode the current page is
 * operating under.
 *
 * Phase 6 driver: the AppShell uses this to decide whether to mount
 * the ShelfSidebar (Orchestrator modes) or suppress the sidebar
 * entirely (Iterator / Operate / Broadcast / Upload+Decompose modes).
 *
 * Resolution order:
 *   1. Explicit ``?mode=…`` query param (URL is the canonical source
 *      while a project is being authored — survives reloads, shareable
 *      with collaborators).
 *   2. Felix's inferred mode from ``modeInferenceApi`` cache (if a
 *      project_id is present in the URL).
 *   3. ``null`` — caller should fall back to its default surface.
 *
 * Falls through to ``null`` rather than guessing — the AppShell uses
 * the null case to mount the legacy V2 ``Sidebar`` (the safe default).
 */

import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import type { ProjectMode } from "@/api/modeInferenceApi";

const VALID_MODES: readonly ProjectMode[] = [
  "iterate",
  "orchestrate_structured",
  "orchestrate_unstructured",
  "upload_decompose",
  "operate",
  "broadcast",
] as const;

/** The mode a project ROUTE represents.
 *
 *  ROW 4b7012f6. Four of the six modes land on a route of their own —
 *  ProjectLanding's routeForMode sends `iterate` to `/iterator`, `operate` to
 *  `/operate`, `broadcast` to `/broadcast` and `upload_decompose` to `/import`
 *  — and only the two Orchestrator modes keep a `?mode=` param. This hook read
 *  the param and nothing else, so on all four of those routes it returned null.
 *
 *  What that cost: AppShell decides the sidebar from this value.
 *  `isSidebarSuppressingMode(null)` is false, so Operate, Broadcast and
 *  Upload+Decompose all mounted the FelixRail instead of the edge-to-edge canvas
 *  the comment in AppShell describes. The Iterator escaped only because AppShell
 *  special-cases its path separately.
 *
 *  The route IS the mode on those four, so read it. No fetch, no guess. */
const MODE_BY_ROUTE_SUFFIX: ReadonlyArray<readonly [string, ProjectMode]> = [
  ["/iterator", "iterate"],
  ["/operate", "operate"],
  ["/broadcast", "broadcast"],
  ["/import", "upload_decompose"],
] as const;

function fromPath(pathname: string): ProjectMode | null {
  // Anchored on /projects/<id>/<suffix> so an unrelated path that happens to
  // end in /import cannot claim a project mode.
  if (!pathname.startsWith("/projects/")) return null;
  for (const [suffix, mode] of MODE_BY_ROUTE_SUFFIX) {
    if (pathname.endsWith(suffix)) return mode;
  }
  return null;
}

function fromQueryParam(search: string): ProjectMode | null {
  try {
    const params = new URLSearchParams(search);
    const mode = params.get("mode");
    if (mode && (VALID_MODES as readonly string[]).includes(mode)) {
      return mode as ProjectMode;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Carry the current `?mode=` onto an outgoing path.
 *
 * THE DOCSTRING ABOVE CLAIMS THE PARAM "survives reloads, shareable with
 * collaborators". True of the URL it is already on, and false the moment anyone
 * clicks anything: every ShelfCard was a plain `to={base + "/objectives"}`, so
 * the first navigation dropped the param, `isOrchestratorMode` went false, and
 * AppShell swapped the ShelfSidebar for the FelixRail mid-session. The mode is
 * chosen in exactly one place (ProjectLanding.tsx:169) and was erased by the
 * first thing the user did with it.
 *
 * This does NOT make the mode durable — nothing writes projects.orchestrator_mode
 * (all 134 rows carry the default 'structured' and
 * Mode.to_project_orchestrator_mode has zero callers), so a fresh entry with no
 * param still resolves to null. That persistence is the rest of row 5d7d4068.
 * What this fixes is navigation erasing a choice the user just made.
 *
 * Returns `to` unchanged when there is no valid mode to carry, so a caller can
 * apply it unconditionally.
 */
export function preserveMode(to: string, search: string): string {
  const mode = fromQueryParam(search);
  if (!mode) return to;
  // A path may already carry its own query string, and an absolute URL is not
  // ours to rewrite.
  if (/^[a-z][a-z0-9+.-]*:/i.test(to)) return to;
  const [path, hash] = to.split("#");
  const sep = path.includes("?") ? "&" : "?";
  // If the target already names a mode, it wins — an explicit destination is
  // not something to override with where the user happened to come from.
  if (/[?&]mode=/.test(path)) return to;
  return `${path}${sep}mode=${encodeURIComponent(mode)}${hash ? `#${hash}` : ""}`;
}

/**
 * The Orchestrator mode last chosen for a project in this browser session.
 *
 * preserveMode carries `?mode=` only through the links that call it (the shelf
 * cards). The overview's tiles and chips, "Related:", the breadcrumbs, the risk
 * links and "Add a phase gate" do not, and each opened the old sidebar mid-tour
 * (measured 2026-10-27 on project 4b942f19: 30 such links across 15 pages,
 * founder 6428293584 (B)). Remembering the mode per project makes every project
 * page keep the rail, whatever link reached it. Only the two Orchestrator modes
 * are remembered: a remembered Iterate must never hide the sidebar elsewhere.
 * "Change mode" still reaches the picker (?pick=1, ProjectLanding).
 */
const MODE_MEMORY_PREFIX = "ftl-project-mode:";

export function rememberOrchestratorMode(projectId: string | null, search: string): void {
  const mode = fromQueryParam(search);
  if (!projectId || !isOrchestratorMode(mode)) return;
  try {
    sessionStorage.setItem(MODE_MEMORY_PREFIX + projectId, mode as string);
  } catch {
    /* storage unavailable: the URL param still works */
  }
}

export function rememberedMode(projectId: string | null): ProjectMode | null {
  if (!projectId) return null;
  try {
    const mode = sessionStorage.getItem(MODE_MEMORY_PREFIX + projectId) as ProjectMode | null;
    return isOrchestratorMode(mode) ? mode : null;
  } catch {
    return null;
  }
}

/** Returns the current project mode if one can be resolved, else null. */
export function useProjectMode(): {
  mode: ProjectMode | null;
  projectId: string | null;
} {
  const location = useLocation();
  const params = useParams();
  const projectId = (params.id ?? params.projectId ?? null) as string | null;

  const mode = useMemo<ProjectMode | null>(() => {
    // Param first: it is the only source for the two Orchestrator modes, and an
    // explicit ?mode= should win if both are somehow present.
    // Then the route's own mode, then the Orchestrator mode chosen earlier in
    // this session for this project (a link that dropped the param).
    return fromQueryParam(location.search) ?? fromPath(location.pathname)
      ?? rememberedMode(projectId);
  }, [location.search, location.pathname, projectId]);

  useEffect(() => {
    rememberOrchestratorMode(projectId, location.search);
  }, [projectId, location.search]);

  return { mode, projectId };
}

/**
 * The mode the shell lays a page out in (row ab7c4e09, founder 6428293584 option (A)).
 *
 * INSIDE A PROJECT THE ORCHESTRATOR RAIL IS THE DEFAULT. Option (B) carried `?mode=` on the tour's
 * links and remembered it per project for the session, but a fresh entry (a link in an email, the
 * command palette, a new tab) still resolved to null and opened the old sidebar. A page under
 * /projects/<id> with no mode from the param, its own route or this session's memory is laid out in
 * the structured Orchestrator mode. The four route modes (Iterator, Operate, Broadcast, Import) keep
 * their own layouts, and pages outside a project are unchanged.
 */
export function shellMode(
  mode: ProjectMode | null,
  projectId: string | null,
  pathname: string,
): ProjectMode | null {
  if (mode) return mode;
  return projectId && pathname.startsWith("/projects/") ? "orchestrate_structured" : null;
}

/** Convenience predicate — true when the surface is one of the
 *  Orchestrator modes that mounts the Shelf Sidebar. */
export function isOrchestratorMode(mode: ProjectMode | null): boolean {
  return (
    mode === "orchestrate_structured" || mode === "orchestrate_unstructured"
  );
}

/** Convenience predicate — true when the surface actively SUPPRESSES
 *  the sidebar (Iterator / Operate / Broadcast / Upload+Decompose). The
 *  AppShell emits ``iterator.sidebar_dismissed_by_iterator_mode``
 *  telemetry in this case so Phase 7 knows the absence is intentional. */
export function isSidebarSuppressingMode(mode: ProjectMode | null): boolean {
  return (
    mode === "iterate" ||
    mode === "operate" ||
    mode === "broadcast" ||
    mode === "upload_decompose"
  );
}
